/**
 * NHTSA vPIC VIN Decoder API Client
 *
 * Decodes Vehicle Identification Numbers (VINs) using the official NHTSA
 * Product Information Catalog (vPIC) API.
 *
 * API Documentation: https://vpic.nhtsa.dot.gov/api/
 *
 * Features:
 * - Free public API (no authentication required)
 * - Returns Year, Make, Model, and other vehicle details
 * - Graceful error handling with meaningful error codes
 * - No CORS issues (public API allows cross-origin requests)
 */

// =============================================================================
// Types
// =============================================================================

export interface VehicleInfo {
  year: number | null;
  make: string;
  model: string;
  trim?: string;
  bodyClass?: string;
  driveType?: string;
  fuelType?: string;
  errorCode?: NhtsaErrorCode;
  errorMessage?: string;
}

export type NhtsaErrorCode =
  | 'INVALID_FORMAT'
  | 'API_ERROR'
  | 'NO_RESULTS'
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'NHTSA_ERROR';

interface NhtsaApiResponse {
  Count: number;
  Message: string;
  SearchCriteria: string;
  Results: NhtsaResult[];
}

interface NhtsaResult {
  ModelYear: string;
  Make: string;
  Model: string;
  Trim: string;
  BodyClass: string;
  DriveType: string;
  FuelTypePrimary: string;
  ErrorCode: string;
  ErrorText: string;
  [key: string]: string; // Allow other fields
}

// =============================================================================
// Configuration
// =============================================================================

const NHTSA_API_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues';
const REQUEST_TIMEOUT_MS = 10000; // 10 seconds

// =============================================================================
// VIN Validation
// =============================================================================

/**
 * Validate VIN format before making API request.
 * VINs must be 17 characters, alphanumeric, excluding I, O, Q.
 */
export function isValidVinFormat(vin: string): boolean {
  if (!vin || typeof vin !== 'string') return false;
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
}

// =============================================================================
// Main Decoder Function
// =============================================================================

/**
 * Decode a VIN using the NHTSA vPIC API.
 *
 * @param vin - The 17-character Vehicle Identification Number
 * @returns Promise resolving to VehicleInfo with decoded data or error info
 *
 * @example
 * ```ts
 * const info = await decodeVin('1HGCM82633A004352');
 * if (!info.errorCode) {
 *   console.log(`${info.year} ${info.make} ${info.model}`);
 * }
 * ```
 */
export async function decodeVin(vin: string): Promise<VehicleInfo> {
  // Validate VIN format first
  if (!isValidVinFormat(vin)) {
    return {
      year: null,
      make: '',
      model: '',
      errorCode: 'INVALID_FORMAT',
      errorMessage: 'VIN must be exactly 17 alphanumeric characters (excluding I, O, Q)'
    };
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${NHTSA_API_URL}/${vin.toUpperCase()}?format=json`, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        Accept: 'application/json'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('[NHTSA] API returned non-OK status:', response.status);
      return {
        year: null,
        make: '',
        model: '',
        errorCode: 'API_ERROR',
        errorMessage: `NHTSA API returned status ${response.status}`
      };
    }

    const data: NhtsaApiResponse = await response.json();

    if (!data.Results || data.Results.length === 0) {
      return {
        year: null,
        make: '',
        model: '',
        errorCode: 'NO_RESULTS',
        errorMessage: 'No results returned from NHTSA API'
      };
    }

    const result = data.Results[0]!;

    // NHTSA returns error codes in the result
    // Error code 0 = success, anything else indicates an issue
    const nhtsaErrorCode = result.ErrorCode;
    if (nhtsaErrorCode && nhtsaErrorCode !== '0' && nhtsaErrorCode !== '') {
      // Some error codes still return partial data, so we continue processing
      // but include the error info for the caller to decide
      const errorText = result.ErrorText || 'Unknown NHTSA error';
      console.warn('[NHTSA] VIN decode returned error code:', nhtsaErrorCode, errorText);

      // If no meaningful data, return error
      if (!result.Make && !result.Model && !result.ModelYear) {
        return {
          year: null,
          make: '',
          model: '',
          errorCode: 'NHTSA_ERROR',
          errorMessage: errorText
        };
      }
      // Otherwise continue with partial data
    }

    // Parse year safely
    const year = result.ModelYear ? parseInt(result.ModelYear, 10) : null;
    const validYear = year && !isNaN(year) && year >= 1900 && year <= 2100 ? year : null;

    return {
      year: validYear,
      make: result.Make || '',
      model: result.Model || '',
      ...(result.Trim && { trim: result.Trim }),
      ...(result.BodyClass && { bodyClass: result.BodyClass }),
      ...(result.DriveType && { driveType: result.DriveType }),
      ...(result.FuelTypePrimary && { fuelType: result.FuelTypePrimary })
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.error('[NHTSA] Request timed out');
        return {
          year: null,
          make: '',
          model: '',
          errorCode: 'TIMEOUT',
          errorMessage: 'Request timed out after 10 seconds'
        };
      }

      console.error('[NHTSA] VIN decode failed:', error.message);
      return {
        year: null,
        make: '',
        model: '',
        errorCode: 'NETWORK_ERROR',
        errorMessage: error.message
      };
    }

    console.error('[NHTSA] VIN decode failed with unknown error:', error);
    return {
      year: null,
      make: '',
      model: '',
      errorCode: 'NETWORK_ERROR',
      errorMessage: 'Unknown error occurred'
    };
  }
}

// =============================================================================
// React Hook Helper
// =============================================================================

/**
 * Format decoded vehicle info for display.
 *
 * @example
 * formatVehicleInfo({ year: 2024, make: 'Honda', model: 'Accord' })
 * // Returns: "2024 Honda Accord"
 */
export function formatVehicleInfo(info: VehicleInfo): string {
  const parts = [info.year?.toString(), info.make, info.model, info.trim].filter(Boolean);

  return parts.join(' ') || 'Unknown Vehicle';
}

/**
 * Check if vehicle info has meaningful data.
 */
export function hasVehicleData(info: VehicleInfo): boolean {
  return !!(info.year || info.make || info.model);
}
